import Handlebars from "handlebars"
import { emit, fail, setVar } from "./events"
import { itemTools, noteIndex, transitionByName } from "./tools"
import { toBoolean, toNumber } from "./vars"

const NEXT: PlaybookOutcome = { type: "next" }
const RESET: PlaybookOutcome = { type: "reset" }

// The way out of an ai item that never depends on the model: it fires the first transition, or next when the
// item declares none
const EXIT = "exit"

const CLASSIFY = "The contact was asked the question below and answered with the message. If the message means one of the options, call its tool. If none fits, call nothing. Never write a reply."

// Fills the run it receives and returns it. It never throws: a failure lands in run.errors and the turn ends
// with whatever it had. The state is copied first, so the one the caller passed is never touched
export async function play<R extends PlaybookRun = PlaybookRun>(init: PlaybookRunInit<R>): Promise<R> {
  const run = init as unknown as R
  run.input ||= {}
  run.state = init.state ? JSON.parse(JSON.stringify(init.state)) : { cursor: null, vars: {} }
  run.output = {}
  run.errors = []

  await emit(run, "playbook.before")
  try {
    await turn(run)
    replies(run)
  }
  catch (e) { await fail(run, e) }
  await emit(run, "playbook.after")
  return run
}

async function turn(run: PlaybookRun) {

  const main = flowItems(run.playbook, "main")
  if (!main.length) return emit(run, "playbook.boot", { note: "the main flow is empty" })

  const { input } = run
  if (input.id && input.id === run.state.last_inbound_id) return emit(run, "playbook.duplicate", { note: `inbound already processed: ${input.id}` })
  if (input.id) run.state.last_inbound_id = input.id

  if (!run.state.cursor) {
    await emit(run, "playbook.boot", { flow: "main", item: main[0].id })
    return runChain(run, { type: "goto", flow: "main", item: main[0].id }, null)
  }

  const item = currentItem(run)
  if (!item) return runChain(run, await lost(run, run.state.cursor), null)

  await emit(run, "answer.before")
  const message = run.input.message || ""
  const outcome = item.type === "ai" ? await answerAI(run, item, message) : await answerItem(run, item, message)
  await emit(run, "answer.after", { outcome: outcome ? label(outcome) : undefined })
  await runChain(run, outcome, run.state.cursor)
}

async function answerItem(run: PlaybookRun, item: PlaybookItem, message: string): Promise<PlaybookOutcome | null> {

  const option = pick(run, item, message) || await classify(run, item, message)
  if (option) return take(run, option)

  if (item.set) {
    await setVar(run, item.set, message.trim())
    await emit(run, "item.captured", { outcome: "next", note: `set ${item.set}` })
    return NEXT
  }

  if (available(run, item).length) {
    await emit(run, "option.no_match", { note: "nothing matched, asking again" })
    await say(run, (item.on_no_match || run.playbook.defaults?.on_no_match)?.say)
    await say(run, item.say)
    if (item.image) show(run, item.image)
    return null
  }

  await emit(run, "item.passed", { outcome: "next", note: "the item declares no way to consume a message" })
  return NEXT
}

async function answerAI(run: PlaybookRun, item: PlaybookItem, message: string): Promise<PlaybookOutcome | null> {

  if (normalize(message) === EXIT) {
    const transition = item.transitions?.[0]
    if (transition) await apply(run, transition.set, transition.inc)
    const outcome = transition?.action || NEXT
    await emit(run, "ai.exit", { outcome: label(outcome), note: "the contact typed exit" })
    return outcome
  }

  if (!run.ai) {
    await emit(run, "ai.missing", { note: "no ai adapter was passed" })
    return null
  }

  const notesScope = `${run.state.cursor!.flow}.${item.id}`
  const notes = item.tools?.some(t => t.type === "notes") ? noteIndex(run.state.notes, notesScope) : undefined
  const context = [item.context ? await interpolate(run, item.context) : undefined, notes].filter(Boolean).join("\n\n")

  try {
    const result = await ask(run, {
      model: run.playbook.config?.model,
      prompt: item.prompt || "",
      context: context || undefined,
      history: run.history,
      tools: itemTools(item, notesScope, run.tools),
      platform: item.tools?.filter(t => t.type === "search" || t.type === "generate" || t.type === "pdf"),
    })

    const transition = result?.transition ? transitionByName(item, result.transition) : undefined
    if (transition) await apply(run, transition.set, transition.inc)

    if (result?.text) await output(run, result.text)
    if (result?.files?.length) (run.output.files ||= []).push(...result.files)
    return transition?.action || null
  }
  catch (e) { await fail(run, e, "the ai adapter failed") }

  return null
}

// ai.before may change the call or answer instead of the model, ai.after may change what came back
async function ask(run: PlaybookRun, call: PlaybookAICall): Promise<PlaybookAIResult | undefined> {
  const before = await emit(run, "ai.before", { ai: call })
  const asked = before.ai || call
  const result = before.result ?? await run.ai!(run, asked)
  const after = await emit(run, "ai.after", { ai: asked, result, calls: result?.calls, note: result?.text || result?.transition ? undefined : "the adapter returned no text" })
  return after.result
}

function pick(run: PlaybookRun, item: PlaybookItem, message: string): PlaybookOption | undefined {
  const value = normalize(message)
  if (!value) return undefined
  return available(run, item).find(o => normalize(o.label) === value || optionId(o) === value || matches(o.match, message))
}

// Only after every deterministic way failed. Each labelled option is offered as a transition tool
async function classify(run: PlaybookRun, item: PlaybookItem, message: string): Promise<PlaybookOption | undefined> {
  const options = available(run, item).filter(o => o.label)
  if (!item.classify || !run.ai || !options.length || !normalize(message)) return undefined

  const tools: PlaybookTool[] = options.map((o, i) => ({ name: `option_${i + 1}`, description: [o.label, ...(o.match || [])].join(" / "), transition: true }))

  try {
    const result = await ask(run, {
      model: run.playbook.config?.classify_model || run.playbook.config?.model,
      prompt: CLASSIFY,
      context: item.say ? await interpolate(run, item.say) : undefined,
      tools,
    })
    const option = options[tools.findIndex(t => t.name === result?.transition)]
    await emit(run, "option.classified", { option: option && optionId(option), note: option ? undefined : "the model picked no option" })
    return option
  }
  catch (e) { await fail(run, e, "the ai adapter failed") }
}

async function take(run: PlaybookRun, option: PlaybookOption): Promise<PlaybookOutcome> {
  await emit(run, "option.before", { option: optionId(option) })
  await apply(run, option.set, option.inc)
  await say(run, option.say)
  await emit(run, "option.after", { option: optionId(option), outcome: label(option.action || NEXT) })
  return option.action || NEXT
}

// set first, then inc on top: a set to 0 and an inc in the same option restart a counter at the inc
async function apply(run: PlaybookRun, set?: { [key: string]: any }, inc?: { [key: string]: number }) {
  for (const [key, value] of Object.entries(set || {})) await setVar(run, key, value)
  for (const [key, amount] of Object.entries(inc || {})) await setVar(run, key, (toNumber(run.state.vars[key]) ?? 0) + amount)
}

// ── Control ─────────────────────────────────────────────────────────────────

async function runChain(run: PlaybookRun, outcome: PlaybookOutcome | null, from: PlaybookCursor | null) {

  let pending = outcome
  let at = from
  let reset = false

  while (pending) {

    if (pending.type === "goto") {
      at = { flow: pending.flow, item: pending.item }
      pending = await enter(run, at)
      continue
    }

    if (pending.type === "reset") {
      if (reset) {
        await emit(run, "flow.reset", { flow: at?.flow, item: at?.item, note: "a second reset in one turn: every item of main is filtered out" })
        return
      }
      reset = true
      at = { flow: "main", item: flowItems(run.playbook, "main")[0].id }
      pending = await enter(run, at)
      continue
    }

    if (pending.type === "next") {
      // Array order defines what "next" means, and nothing else
      const items = at ? flowItems(run.playbook, at.flow) : []
      const next = items[items.findIndex(i => i.id === at?.item) + 1]

      if (!at || !next) {
        await emit(run, "flow.end", { flow: at?.flow, item: at?.item, outcome: "reset", note: "ran past the last item of the flow" })
        pending = RESET
        continue
      }

      at = { flow: at.flow, item: next.id }
      pending = await enter(run, at)
      continue
    }

    await emit(run, "item.lost", { flow: at?.flow, item: at?.item, note: `unknown action: ${(pending as any)?.type}` })
    return
  }
}

async function enter(run: PlaybookRun, cursor: PlaybookCursor): Promise<PlaybookOutcome | null> {

  const item = getItem(run.playbook, cursor)
  if (!item) return lost(run, cursor)

  await emit(run, "item.before", { ...cursor })
  if (!meetsAny(item.conditions, run)) {
    await emit(run, "item.skipped", { ...cursor, outcome: "next", note: `conditions not met: ${item.conditions!.map(c => c.join(" ")).join(" | ")}` })
    return NEXT
  }

  if (!item.say && !item.image && item.type !== "ai") {
    await emit(run, "item.skipped", { ...cursor, outcome: "next", note: "the item has nothing to say" })
    return NEXT
  }

  run.state.cursor = { ...cursor }
  await emit(run, "item.entered", { note: "waiting for a message" })
  await say(run, item.say)
  if (item.image && item.type !== "ai") show(run, item.image)
  return null
}

async function lost(run: PlaybookRun, cursor: PlaybookCursor): Promise<PlaybookOutcome | null> {
  const target = run.playbook.config?.on_lost || { flow: "main", item: flowItems(run.playbook, "main")[0].id }

  if (!getItem(run.playbook, target)) {
    run.state.cursor = null
    await emit(run, "item.lost", { note: `${cursor.flow}.${cursor.item} is gone and so is on_lost, closing the session` })
    return null
  }

  await emit(run, "item.lost", { note: `${cursor.flow}.${cursor.item} is gone, going to ${target.flow}.${target.item}` })
  return { type: "goto", flow: target.flow, item: target.item }
}

// ── Playbook reads ──────────────────────────────────────────────────────────

function available(run: PlaybookRun, item: PlaybookItem | null): PlaybookOption[] {
  return (item?.options || []).filter(o => meetsAny(o.conditions, run))
}

function flowItems(playbook: Playbook, flow: string): PlaybookItem[] {
  return playbook.flows?.[flow]?.items || []
}

function getItem(playbook: Playbook, cursor: PlaybookCursor): PlaybookItem | null {
  return flowItems(playbook, cursor?.flow).find(i => i.id === cursor?.item) || null
}

function currentItem(run: PlaybookRun): PlaybookItem | null {
  return run.state.cursor ? getItem(run.playbook, run.state.cursor) : null
}


// ── Pure helpers ────────────────────────────────────────────────────────────

export function normalize(text: string): string {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[\p{Extended_Pictographic}\p{Regional_Indicator}\uFE0F\u200D]/gu, "")
    .replace(/\s+/g, " ")
    .trim()
}

function optionId(option: PlaybookOption): string {
  return option.id || normalize(option.label)
}

function matches(list: string[] | undefined, text: string): boolean {
  const value = normalize(text)
  return !!value && (list || []).some(m => normalize(m) === value)
}

// Empty/absent always holds. Otherwise any one of the list holding is enough
function meetsAny(conditions: PlaybookCondition[] | undefined, run: PlaybookRun): boolean {
  return !conditions || conditions.length === 0 || conditions.some(c => meets(c, run))
}

function meets(condition: PlaybookCondition, run: PlaybookRun): boolean {
  const name = condition[0]
  const left = name.startsWith("global.") ? run.global?.[name.slice("global.".length) as keyof PlaybookGlobals] : run.state.vars?.[name]
  if (condition.length === 1) return toBoolean(left) ?? (typeof left === "string" ? !!left.trim() : !!left)

  if (condition.length === 2) {
    const set = left !== undefined && left !== null && left !== ""
    return condition[1] === "isSet" ? set : !set
  }

  const right = condition[2]
  if (condition[1] === "==") return same(left, right)
  if (condition[1] === "!=") return !same(left, right)

  // An ordering is numeric: a side that holds no number makes it false, whichever way it points
  const a = toNumber(left), b = toNumber(right)
  if (a === undefined || b === undefined) return false
  switch (condition[1]) {
    case ">": return a > b
    case ">=": return a >= b
    case "<": return a < b
    case "<=": return a <= b
  }
}

// The literal on the right says what kind of equality it is
function same(left: any, right: any): boolean {
  if (typeof right === "number") return toNumber(left) === right
  if (typeof right === "boolean") return toBoolean(left) === right
  if (typeof right === "string" && left !== undefined && left !== null) return normalize(String(left)) === normalize(right)
  return left === right
}

// ── Templates ───────────────────────────────────────────────────────────────

const hbs = Handlebars.create()

const templates = new Map<string, ReturnType<typeof Handlebars.compile>>()

function compile(text: string, strict: boolean) {
  const key = (strict ? "!" : "") + text
  let template = templates.get(key)

  if (!template) {
    template = hbs.compile(text, { noEscape: true, strict })
    templates.set(key, template)
  }
  return template
}

async function interpolate(run: PlaybookRun, text: string): Promise<string> {
  if (!text.includes("{{")) return text

  const scope = { ...run.state.vars, global: run.global || {} }
  try { return compile(text, true)(scope) }
  catch (e: any) {
    await emit(run, "template.missing", { note: `${e?.message || e}` })
    try { return compile(text, false)(scope) } catch { return text }
  }
}

function label(outcome: PlaybookOutcome): string {
  return outcome.type === "goto" ? `goto:${outcome.flow}.${outcome.item}` : outcome.type
}

// ── Output ──────────────────────────────────────────────────────────────────

async function say(run: PlaybookRun, text: string | undefined) {
  if (text) await output(run, await interpolate(run, text))
}

// Every text that goes out, the model's too. say.before may rewrite it, or empty it to drop it
async function output(run: PlaybookRun, text: string) {
  const { text: final } = await emit(run, "say.before", { text })
  if (!final) return
  run.output.response = run.output.response ? `${run.output.response}\n\n${final}` : final
  await emit(run, "say.after", { text: final })
}

function show(run: PlaybookRun, image: string) {
  (run.output.images ||= []).push(image)
}

// The options of the item where the cursor came to rest, only when the turn said something
function replies(run: PlaybookRun) {
  if (!run.output.response && !run.output.images?.length) return
  const replies = available(run, currentItem(run)).filter(o => o.label).map(o => ({ id: optionId(o), label: o.label }))
  if (replies.length) run.output.quick_replies = replies
}
